import {Feature} from "ol";
import type {Coordinate} from "ol/coordinate";
import Point from "ol/geom/Point";
import type Layer from "ol/layer/Layer";
import type OlMap from "ol/Map";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import type Overlay from "ol/Overlay";
import type {Positioning} from "ol/Overlay";
import {transform} from "ol/proj";
import type VectorSource from "ol/source/Vector";
import {Icon, Style} from "ol/style";

import targetIcon from "../images/target.svg";
import {EPSG_3857} from "./maps";

const withPrefix = (keys: string[]): string[] => keys.map((k) => (k ? "location.quality." + k : k));
// Mikrolage nutzt qualitiesPrimary, Makrolage qualitiesPrimaryMacro (eigene Klassen seit #19261).
export const qualitiesPrimary = withPrefix(["", "simple", "moderate", "average", "good", "veryGood"]);
export const qualitiesPrimaryMacro = withPrefix(["", "verySimple", "simple", "average", "good", "veryGood"]);
// Deklinierte Form der Makrolage-Klassen, für "innerhalb der ... Makrolage" im Mikrolage-Tooltip.
export const qualitiesSecondary = withPrefix([
    "",
    "verySimpleDeclined",
    "simpleDeclined",
    "averageDeclined",
    "goodDeclined",
    "veryGoodDeclined",
]);
export const tooltipMarkerLayer = "tooltip-markers";

/** A layer tagged with the custom REVA layer key. */
type RevaLayer = Layer & {reva_key?: string};

/** Callback to resolve location data for a coordinate. */
type GetLocationData = (coordinate: Coordinate) => Promise<unknown>;
/** Callback to render the tooltip text for the resolved location data. */
type GetTooltipText = (locationData: unknown) => unknown;

let hoverTimeoutId: ReturnType<typeof setTimeout> | null = null;
let hoverLastCoordinate: Coordinate | null = null;

// Karten, deren Viewport bereits einen pointerleave-Listener trägt (idempotent,
// da tooltipHandler pro Event aufgerufen wird und mehrere Karten-Tabs dasselbe
// modulweite Hover-State teilen).
const leaveListenerRegistered = new WeakSet<OlMap>();
// Karten, über deren Viewport sich der Mauszeiger aktuell befindet.
const pointerInside = new WeakSet<OlMap>();

// Verhindert, dass der Hover-Tooltip nach Verlassen der Karte noch aufpoppt:
// beim pointerleave den laufenden Hover-Timeout abbrechen und den Zustand so
// zurücksetzen, dass ein bereits gestarteter (async) Hover-Handler nichts mehr anzeigt.
function ensureLeaveListener(map: OlMap): void {
    if (leaveListenerRegistered.has(map)) return;
    leaveListenerRegistered.add(map);
    map.getViewport()?.addEventListener("pointerleave", () => {
        pointerInside.delete(map);
        clearTimeout(hoverTimeoutId ?? undefined);
        hoverTimeoutId = null;
        hoverLastCoordinate = null;
    });
}

export function tooltipHandler(
    map: OlMap,
    evt: MapBrowserEvent,
    getLocationData: GetLocationData,
    getTooltipText: GetTooltipText,
): void {
    if (evt.dragging) return;
    ensureLeaveListener(map);
    pointerInside.add(map);
    clearTooltipLayer(map);
    if (evt.type === "click") {
        void clickHandler(map, evt, getLocationData, getTooltipText);
        return;
    }
    if (evt.type === "pointermove") {
        hoverHandler(evt, map, () => void clickHandler(map, evt, getLocationData, getTooltipText, true));
    }
}

async function clickHandler(
    map: OlMap,
    evt: MapBrowserEvent,
    getLocationData: GetLocationData,
    getTooltipText: GetTooltipText,
    fromHover = false,
): Promise<void> {
    try {
        const {tooltip, locationData} = await getDataAndTooltip(map, evt, getLocationData);
        if (!tooltip) {
            return;
        }
        // Maus hat die Karte während des Daten-Ladens verlassen → Hover-Tooltip
        // nicht mehr anzeigen (Klick-Tooltips bleiben unverändert).
        if (fromHover && !pointerInside.has(map)) {
            tooltip.style.visibility = "hidden";
            return;
        }
        if (!locationData) {
            return;
        }
        if (fromHover && evt.coordinate.toString() !== hoverLastCoordinate?.toString()) {
            return;
        }

        const tooltipText = getTooltipText(locationData);
        // Kein Tooltip UND kein Fadenkreuz-Marker, wenn getTooltipText keinen
        // (nicht-leeren) Text liefert — z.B. bei fehlenden Daten außerhalb der
        // Layer-Abdeckung (Abfrage außerhalb Deutschlands).
        if (typeof tooltipText !== "string" || tooltipText === "") {
            tooltip.style.visibility = "hidden";
            return;
        }
        // Marker erst setzen, wenn ein Tooltip wirklich angezeigt wird.
        addTooltipMarker(map, evt);
        tooltip.innerHTML = tooltipText;
        repositionTooltip(map, evt.coordinate, tooltip);
    } catch (err) {
        // Scheitert das Laden der Standortdaten (getLocationData) oder das Rendern des
        // Textes, bleibt der Tooltip einfach aus — bewusst keine technische Fehlermeldung
        // für den Nutzer, nur ein Log. Der Fadenkreuz-Marker kann zu diesem Zeitpunkt
        // schon gesetzt sein (addTooltipMarker läuft vor dem Positionieren), deshalb hier
        // mit weg.
        console.error("Could not build the map tooltip", err);
        hideTooltip(map);
        clearTooltipLayer(map);
    }
}

/** Tooltip-Overlay ausblenden, ohne seinen Inhalt anzufassen. */
function hideTooltip(map: OlMap): void {
    const tooltip = getOverlay(map)?.getElement();
    if (tooltip) {
        tooltip.style.visibility = "hidden";
    }
}

// Der Tooltip öffnet standardmäßig mittig nach oben (positioning bottom-center). Nahe an
// den Kartenrändern würde er abgeschnitten — dann zur jeweils anderen Seite aufklappen:
// oben -> nach unten, links -> nach rechts, rechts -> nach links.
function repositionTooltip(map: OlMap, coordinate: Coordinate, tooltip: HTMLElement): void {
    const overlay = getOverlay(map);
    const pixel = map.getPixelFromCoordinate(coordinate);
    if (!overlay || !pixel) return;
    const margin = 20;
    const vertical = pixel[1] >= tooltip.offsetHeight + margin ? "bottom" : "top";
    const mapWidth = map.getSize()?.[0] ?? Number.MAX_SAFE_INTEGER;
    const halfWidth = tooltip.offsetWidth / 2;
    let horizontal = "center";
    if (pixel[0] < halfWidth + margin) horizontal = "left";
    else if (pixel[0] > mapWidth - halfWidth - margin) horizontal = "right";
    overlay.setPositioning(`${vertical}-${horizontal}` as Positioning);
    overlay.setOffset([0, vertical === "bottom" ? -10 : 10]);
}

function getOverlay(map: OlMap): Overlay | null {
    return map.getOverlayById("tooltipOverlay");
}

function getTooltipLayer(map: OlMap): Layer<VectorSource> | undefined {
    return map.getAllLayers().find((l) => (l as RevaLayer).reva_key === tooltipMarkerLayer) as
        | Layer<VectorSource>
        | undefined;
}

function clearTooltipLayer(map: OlMap): void {
    const markerLayer = getTooltipLayer(map);
    if (!markerLayer) return;
    markerLayer.getSource()?.clear();
}

function addTooltipMarker(map: OlMap, evt: MapBrowserEvent): void {
    const markerLayer = getTooltipLayer(map);
    if (!markerLayer) return;
    clearTooltipLayer(map);
    const feature = new Feature(new Point(evt.coordinate));
    const style = new Style({
        image: new Icon({
            anchorXUnits: "pixels",
            anchorYUnits: "pixels",
            anchor: [25, 25],
            src: targetIcon,
            scale: 0.45,
        }),
    });
    feature.setStyle(style);
    markerLayer.getSource()?.addFeature(feature);
}

async function getDataAndTooltip(
    map: OlMap,
    evt: MapBrowserEvent,
    getLocationData: GetLocationData,
): Promise<{tooltip?: HTMLElement; locationData?: unknown}> {
    const coordinate = transform(evt.coordinate, EPSG_3857, "EPSG:4326");
    const locationData = await getLocationData(coordinate);
    const overlay = getOverlay(map);

    const tooltip = overlay?.getElement();
    if (!tooltip || !overlay || !locationData) {
        return {};
    }

    overlay.setPosition(evt.coordinate);
    tooltip.style.visibility = "visible";
    return {tooltip, locationData};
}

const hoverHandler = (evt: MapBrowserEvent, map: OlMap, clickHandler: () => void) => {
    if (evt.coordinate.toString() === hoverLastCoordinate?.toString()) {
        return;
    }
    clearTimeout(hoverTimeoutId ?? undefined);
    hoverLastCoordinate = evt.coordinate;
    hoverTimeoutId = setTimeout(clickHandler, 300);

    const tooltip = getOverlay(map)?.getElement();
    if (tooltip && tooltip.style.visibility === "visible") {
        tooltip.style.visibility = "hidden";
    }
};
